'use client'
import { type ComponentPropsWithoutRef, useEffect, useState } from 'react'
import clsx from 'clsx'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import qs from 'qs'

import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import BackToTopButton from 'shared/ui/BackToTopButton'
import SelectDropdown from 'shared/ui/form/Select'
import InfiniteScrollAnchor from 'shared/ui/InfiniteScrollAnchor'
import LoadingOverlay from 'shared/ui/LoadingOverlay'
import { UPSELLING_LISTING_LIMIT } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { viewProductList } from '../analytics'
import { getNextUpsellingPage } from '../api/products'
import ProductCard from '../components/ProductCard'
import useInfiniteScroll from '../hooks/useInfiniteScroll'
import { ProductMetaTypes, ProductTypes } from '../types/products'
import { parseQueryFilters } from '../utils/filters'
import {
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValueForUpselling,
  SORTING_OPTIONS
} from '../utils/sorting'

import styles from './BestsellersPage.module.scss'

const BESTSELLERS_LIST_NAME = SeoListName.BESTSELLERS_LIST_NAME

interface BestsellersClientProps {
  initialProducts: ProductTypes[]
  initialMeta: ProductMetaTypes
  token: string
  queryParams: Record<string, string | string[]>
}

export default function BestsellersPage({
  initialProducts,
  token,
  queryParams
}: BestsellersClientProps) {
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [sorting, setSorting] = useState<string | string[]>('recommended')

  const { productsData, isFetching, ref } = useInfiniteScroll({
    products: initialProducts,
    query: queryParams,
    getNextPageCallback: getNextUpsellingPage,
    token,
    limit: UPSELLING_LISTING_LIMIT
  })

  const handleSortingChange = async (_, value: string | string[]) => {
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValueForUpselling(value)
    const currentParams = Object.fromEntries(searchParams?.entries() || [])
    const parsedQuery = parseQueryFilters(currentParams)

    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...parsedQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.push(`${pathname}${decodeURIComponent(params)}`)
  }

  useEffect(() => {
    const currentParams = Object.fromEntries(searchParams?.entries() || [])
    const sortingToCheck = {
      sort_by: currentParams.sort_by,
      order: currentParams.order
    }

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
  }, [searchParams, sorting])

  useEffect(() => {
    if (session?.status !== 'loading' && isNotNilOrEmpty(productsData)) {
      viewProductList({
        user,
        products: productsData || [],
        listName: BESTSELLERS_LIST_NAME
      })
    }
  }, [session, productsData, user])

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>Specjały Lokalnego Rolnika</h1>
        <SortingInputWrapper>
          <SortingLabel>Sortuj:</SortingLabel>
          <SelectDropdown
            variant='ghost'
            noMargin
            options={SORTING_OPTIONS}
            name='sorting'
            value={sorting}
            onChange={handleSortingChange}
            id={'bestsellers-sorting'}
          />
        </SortingInputWrapper>
      </div>
      <span data-title='top' />
      <div className={styles.wrapper}>
        <BackToTopButton target='*[data-title="top"]' />
        {productsData &&
          productsData.map((product: ProductTypes, index: number) => {
            return (
              <ProductCard
                key={`${product.id}${index}`}
                product={product}
                productPath={product.slug}
                seoButtonPlace={pathname || ''}
                listName={BESTSELLERS_LIST_NAME}
                idxOnList={index}
                id={`bestsellers_page_product_${product.id}`}
              />
            )
          })}
        <InfiniteScrollAnchor ref={ref} />
      </div>
      {isFetching && <LoadingOverlay />}
    </>
  )
}

export const SortingInputWrapper = ({
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'>) => (
  <div className={clsx(styles.sortingInputWrapper, className)} {...rest} />
)

export const SortingLabel = ({
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'>) => (
  <div className={clsx(styles.sortingLabel, className)} {...rest} />
)
